import axios from "axios";
import APIURLMaker from "../utils/APIURLMaker";
import { getAuthorizationHeaders } from "./authToken";

export const userapi = {
  login: async (username, password, newPassword = null) => {
    try {
      const url = APIURLMaker("/auth/login");
      const loginData = {
        username,
        password,
        ...(newPassword && { newPassword }),
      };

      console.log("Attempting login with:", loginData);

      // For password change, we need to handle 401 errors differently
      if (newPassword) {
        try {
          const response = await axios.post(url, loginData, {
            headers: {
              "Content-Type": "application/json",
            },
          });

          console.log("Password change response:", response.data);
          return response.data;
        } catch (error) {
          // For password change, we don't want to throw an error
          // Just log it and return null to indicate success
          // console.log("Password change completed");
          // return null;
          console.error(
            "Password change API error:",
            error.response?.data || error.message,
          );
          throw new Error(
            error.response?.data?.message || "Password change failed",
          );
        }
      }

      // Regular login attempt
      const response = await axios.post(url, loginData, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      console.log("Login response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Giriş hatası:", error);

      // Check if this is a network/connectivity error (no response from server)
      const isNetworkError =
        error.code === "ERR_NETWORK" ||
        error.code === "ERR_CONNECTION_REFUSED" ||
        error.code === "ERR_CONNECTION_TIMED_OUT" ||
        (error.isAxiosError && !error.response);
      if (isNetworkError) {
        throw new Error(
          "Cannot reach server. Please check your connection or try again later.",
        );
      }

      // Check if this is a credentials expired error
      if (
        error.response?.data?.errorCode === "CREDENTIALS_EXPIRED" ||
        error.response?.data?.message?.includes("CREDENTIALS_EXPIRED")
      ) {
        throw new Error("CREDENTIALS_EXPIRED");
      }

      // For other errors (e.g. 401 wrong credentials), throw a generic error
      throw new Error("Invalid login");
    }
  },

  getAllUsers: async (roleFilter = null) => {
    try {
      const roleQuery = roleFilter ? `?roleName=${roleFilter}` : "";
      const url = APIURLMaker(`/user/getAllUsers${roleQuery}`);

      const response = await axios.get(url, {
        headers: {
          Accept: "*/*",
          "Content-Type": "application/json",
          ...getAuthorizationHeaders(),
        },
      });

      if (response.status !== 200) {
        if (response.status === 403) {
          throw new Error("Yetkiniz bulunmamaktadır.");
        }
        throw new Error("Kullanıcı listesi alınamadı.");
      }

      return response.data;
    } catch (error) {
      console.error("Kullanıcı listesi alma hatası:", error);
      throw error;
    }
  },

  registerUser: async (userData) => {
    try {
      const url = APIURLMaker("/auth/registerUser");

      const requestData = {
        username: userData.username,
        name: userData.name,
        surname: userData.surname,
        telno: userData.phoneNumber,
        roles: userData.roles.map((role) => ({ name: role })),
      };

      const response = await axios.post(url, requestData, {
        headers: {
          "Content-Type": "application/json",
          ...getAuthorizationHeaders(),
        },
      });

      if (response.status !== 200) {
        throw new Error(
          response.data?.message || "Kullanıcı kaydı başarısız oldu.",
        );
      }

      return response.data;
    } catch (error) {
      console.error("Kullanıcı kayıt hatası:", error);
      throw error;
    }
  },

  updateUser: async (userData) => {
    try {
      const url = APIURLMaker("/auth/updateUser");

      const updateData = {
        username: userData.username,
        roles: userData.roles.map((role) => ({ name: role })),
        enabled: userData.enabled,
      };

      if (userData.password) {
        updateData.password = userData.password;
      }

      const response = await axios.post(url, updateData, {
        headers: {
          "Content-Type": "application/json",
          ...getAuthorizationHeaders(),
        },
      });

      if (response.status !== 200) {
        throw new Error(
          response.data.message || "Kullanıcı güncellemesi başarısız oldu.",
        );
      }

      return response.data;
    } catch (error) {
      console.error("Kullanıcı güncelleme hatası:", error);
      throw error;
    }
  },

  getDeliveryPersons: async () => {
    try {
      const url = APIURLMaker("/user/getAllUsers?roleName=ROLE_USER");

      const response = await axios.get(url, {
        headers: {
          Accept: "*/*",
          "Content-Type": "application/json",
          ...getAuthorizationHeaders(),
        },
      });

      if (response.status !== 200) {
        throw new Error("Teslimat görevlileri alınamadı.");
      }

      return response.data;
    } catch (error) {
      console.error("Error fetching delivery persons:", error);
      throw error;
    }
  },

  updatePassword: async (username, currentPassword, newPassword) => {
    try {
      const urlUsers = APIURLMaker("/user/getAllUsers");

      const response = await axios.get(urlUsers, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      const users = response.data;
      const user = users.find((u) => u.username === username);

      if (!user) {
        throw new Error("Kullanıcı bulunamadı");
      }

      const updateData = {
        username: user.username,
        name: user.name,
        surname: user.surname,
        telno: user.telno,
        password: newPassword,
        initialPassword: currentPassword,
        enabled: true,
        roles: user.roles,
      };

      const urlUpdateUser = APIURLMaker("/auth/updateUser");

      const updateResponse = await axios.post(urlUpdateUser, updateData, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (updateResponse.status !== 200) {
        throw new Error(
          updateResponse.data.message || "Şifre güncelleme başarısız oldu.",
        );
      }

      return updateResponse.data;
    } catch (error) {
      console.error("Şifre güncelleme hatası:", error);
      throw error;
    }
  },

  dropUser: async (username) => {
    try {
      const url = APIURLMaker("/auth/dropUser");

      const response = await axios.post(
        url,
        { username },
        {
          headers: {
            "Content-Type": "application/json",
            ...getAuthorizationHeaders(),
          },
        },
      );

      // At this point, status is 2xx
      if (response.status === 200) {
        try {
          // If backend sends a JSON response for 200 OK
          return JSON.parse(response.data);
        } catch {
          // If backend sends a non-JSON response for 200 OK
          return response.data;
        }
      } else if (response.status === 204) {
        // HTTP 204 No Content is a success signal for deletion
        return undefined;
      } else {
        // Any other 2xx status is unexpected for this operation
        throw new Error(
          response.data?.message ||
            `Kullanıcı silme işlemi beklenmedik bir durumla karşılaştı (status: ${response.status}).`,
        );
      }
    } catch (error) {
      // Handle Axios errors (e.g., 4xx, 5xx responses)
      if (error.isAxiosError && error.response) {
        if (error.response.status === 400) {
          // SPECIFIC INTERVENTION: Based on observation, a 400 from this endpoint
          // results in the user being deleted. Treat as client-side success.
          console.warn(
            `User deletion for '${username}' received HTTP 400 from backend. ` +
              `Treating as success on client-side due to observed behavior. Response data:`,
            error.response.data,
          );
          return undefined; // Signal success to the caller by not throwing
        }
        // For other HTTP errors (401, 403, 500, etc.), the error will propagate
        // as it's caught by the generic console.error and throw below.
      }
      // Log and re-throw for non-Axios errors, network issues, or unhandled Axios errors
      console.error("Kullanıcı silme hatası:", error);
      throw error;
    }
  },
};

export default userapi;
